import { useMemo } from "react";
import { isVideoSeason, isVideoSpotLiveSeason, isVideoLegacySeason } from "~/components/blocks/product/type-guard";
import { useQueryString } from "~/hooks/use-query-string";
import type { G_GetVideoQuery } from "~/lib/graphql";

type UseProductBannerArgs = { videoData: G_GetVideoQuery["video"] };

export const useProductBanner = (args: UseProductBannerArgs) => {
  const { videoData } = args;

  const { content } = useQueryString();

  /** 無料のスポット配信かどうかの判定式に使用する */
  const freeProduct = useMemo(() => {
    // スポット配信の場合
    if (isVideoSpotLiveSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return;

      return edges[0].node.freeProduct;
    }

    return;
  }, [videoData]);

  /** プレミアムタグの表示ロジックの判定式に使用する */
  const svodProduct = useMemo(() => {
    // エピソード末端の複数話の場合
    if (isVideoSeason(videoData) && content !== undefined) {
      // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
      if (!videoData.episode) return;

      const {
        episode: { svodProduct },
      } = videoData;

      return svodProduct;
    }

    // エピソード末端でない複数話 もしくは 単数話 もしくは スポット配信の場合
    if (isVideoSeason(videoData) || isVideoSpotLiveSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return;
      return edges[0].node.svodProduct;
    }

    // グラビアの場合
    if (isVideoLegacySeason(videoData)) {
      const {
        content: { svodProduct },
      } = videoData;

      return svodProduct;
    }

    return;
  }, [videoData]);

  const ppvExpiration = useMemo(() => {
    // エピソード末端の複数話の場合
    if (isVideoSeason(videoData) && content !== undefined) {
      // MEMO : ?season=AAA から ?season=AAA&content=BBB にブラウザバックした時に TypeError を防ぐための処理
      if (!videoData.episode) return;

      const {
        episode: { ppvExpiration },
      } = videoData;

      return ppvExpiration;
    }

    // エピソード末端でない複数話 もしくは 単数話 もしくは スポット配信の場合
    if (isVideoSeason(videoData) || isVideoSpotLiveSeason(videoData)) {
      const {
        episodes: { edges },
      } = videoData;

      if (edges.length === 0) return;
      return edges[0].node.ppvExpiration;
    }

    // グラビアの場合
    if (isVideoLegacySeason(videoData)) {
      const { content } = videoData;
      return content.ppvExpiration;
    }

    return;
  }, [videoData]);

  return {
    freeProduct,
    svodProduct,
    ppvExpiration,
  };
};
